import { useState, useEffect, useCallback, useRef } from "react";
import { BACKEND_URL, AI_ENGINE_URL } from "../utils/apiConfig";
import { authFetch } from "../utils/authHelpers";
import { isDagstaatTaak, isTaakAfgerond } from '../utils/taakStatus';

const POLL_INTERVAL = 5 * 60 * 1000;
const SEVERITY_ORDER = { red: 0, orange: 1, blue: 2 };
const GROUP_ORDER = {
  klant: 0,
  workflow: 1,
  persoonlijk: 2,
  feedback: 3,
};

const startOfDay = (value = new Date()) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : startOfDay(date);
};

const formatDate = (date) =>
  date.toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "short",
  });

const formatRelativeLabel = (days) => {
  if (days === null || days === undefined) return "Geen datum";
  if (days < 0) return `${Math.abs(days)} dag${Math.abs(days) === 1 ? "" : "en"} te laat`;
  if (days === 0) return "Vandaag";
  if (days === 1) return "Morgen";
  return `Over ${days} dagen`;
};

const isInternalProject = (name) =>
  Boolean(name && name.replace(/^\d+\.\s*/, "").startsWith("Full Frame"));

const sortNotifications = (notifications) =>
  [...notifications].sort((a, b) => {
    const severityDiff =
      (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99);
    if (severityDiff !== 0) return severityDiff;

    const groupDiff = (GROUP_ORDER[a.group] ?? 99) - (GROUP_ORDER[b.group] ?? 99);
    if (groupDiff !== 0) return groupDiff;

    const aDate = parseDate(a.sortDate);
    const bDate = parseDate(b.sortDate);
    if (aDate && bDate) return aDate - bDate;
    if (aDate) return -1;
    if (bDate) return 1;

    return String(a.title || "").localeCompare(String(b.title || ""));
  });

const dedupeNotifications = (notifications) => {
  const map = new Map();
  notifications.forEach((notification) => {
    if (!map.has(notification.id)) {
      map.set(notification.id, notification);
    }
  });
  return [...map.values()];
};

const computeTaskNotifications = (tasks = []) => {
  const items = [];
  const today = startOfDay();
  const threeDaysFromNow = new Date(today);
  threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);

  for (const task of tasks) {
    const f = task.fields || {};
    const id = task.id;

    if (isTaakAfgerond(f)) { // FU-17: miste Gearchiveerd
      continue;
    }

    if (["Vanallesuurtje", "Dagstart"].includes(f.PrioNr) || f.Type === "Vanallesuurtje") {
      continue;
    }

    const deadline = f.Deadline ? new Date(f.Deadline) : null;
    const endDate = f.EndDateUitvoerder ? new Date(f.EndDateUitvoerder) : null;
    const dueDate = deadline || endDate;
    if (!dueDate || Number.isNaN(dueDate.getTime())) continue;

    dueDate.setHours(0, 0, 0, 0);
    const projectLabel = f.ProjectNummer?.[0] ? ` - ${f.ProjectNummer[0]}` : "";

    // Een voorbije draaidag of vrije dag is geen verlopen deadline (Joris, 3 nov 2026).
    if (dueDate < today && isDagstaatTaak(f)) continue;

    if (dueDate < today) {
      items.push({
        id: `task-overdue-${id}`,
        type: "overdue",
        group: "persoonlijk",
        severity: "red",
        title: f.NaamTaak || "Naamloze taak",
        subtitle: `Deadline verlopen: ${formatDate(dueDate)}${projectLabel}`,
        link: "/dashboard",
        recordId: id,
        sortDate: dueDate.toISOString(),
      });
    } else if (dueDate <= threeDaysFromNow) {
      const daysLeft = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
      items.push({
        id: `task-soon-${id}`,
        type: "due_soon",
        group: "persoonlijk",
        severity: "orange",
        title: f.NaamTaak || "Naamloze taak",
        subtitle: `Deadline: ${formatRelativeLabel(daysLeft)}${projectLabel}`,
        link: "/dashboard",
        recordId: id,
        sortDate: dueDate.toISOString(),
      });
    }
  }

  return items;
};

const computeProjectNotifications = (projects = [], userName) => {
  const items = [];
  const nameLower = userName?.toLowerCase();

  for (const project of projects) {
    const f = project.fields || {};

    if (f.Status === "On Hold") {
      items.push({
        id: `project-hold-${project.id}`,
        type: "on_hold",
        group: "workflow",
        severity: "blue",
        title: f.Projectnaam || f.Projectnummer || "Project",
        subtitle: "Project staat on hold",
        link: `/projects/${project.id}`,
        recordId: project.id,
      });
    }

    if (f.Status !== "Offerte" || !nameLower) continue;

    const plNames =
      f["Projectleider (Volledige naam)"] ||
      f.Projectleider ||
      f.projectleider_naam ||
      [];

    const isPL = Array.isArray(plNames)
      ? plNames.some((name) => name?.toLowerCase() === nameLower)
      : typeof plNames === "string" && plNames.toLowerCase() === nameLower;

    if (isPL) {
      items.push({
        id: `project-offerte-${project.id}`,
        type: "new_offerte",
        group: "workflow",
        severity: "blue",
        title: f.Projectnaam || f.Projectnummer || "Nieuwe offerte",
        subtitle: "Nieuwe offerte om te bekijken",
        link: `/projects/${project.id}`,
        recordId: project.id,
      });
    }
  }

  return items;
};

const computeDeadlineNotifications = (deadlinePayload = {}) => {
  const items = [];
  const deadlines = Array.isArray(deadlinePayload.byDate) ? deadlinePayload.byDate : [];

  for (const deadline of deadlines) {
    if (isInternalProject(deadline.projectName)) continue;
    if (deadline.type === "task" && !deadline.deliverableId) continue;
    if (deadline.daysUntil > 7) continue;

    const severity =
      deadline.daysUntil <= 0 ? "red" : deadline.daysUntil <= 2 ? "orange" : "blue";
    const titlePrefix = deadline.type === "deliverable" ? "Klantdeadline" : "Oplevertaak";

    items.push({
      id: `deadline-${deadline.type}-${deadline.id}`,
      type: "client_deadline",
      group: "klant",
      severity,
      title: `${titlePrefix}: ${deadline.name}`,
      subtitle: `${deadline.projectName || "Project"} - ${formatRelativeLabel(deadline.daysUntil)}`,
      link: deadline.projectId ? `/projects/${deadline.projectId}` : "/dashboard",
      recordId: deadline.id,
      sortDate: deadline.deadline,
    });
  }

  return items;
};

const computeDraaidagNotifications = (draaidagen = [], userEmail, userName) => {
  const items = [];
  const today = startOfDay();
  const tenDaysFromNow = new Date(today);
  tenDaysFromNow.setDate(tenDaysFromNow.getDate() + 10);
  const fiveDaysFromNow = new Date(today);
  fiveDaysFromNow.setDate(fiveDaysFromNow.getDate() + 5);

  for (const dag of draaidagen) {
    const startDate = dag.startDatum ? new Date(dag.startDatum) : null;
    if (!startDate || Number.isNaN(startDate.getTime())) continue;
    startDate.setHours(0, 0, 0, 0);

    const daysUntil = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
    const emailLower = userEmail?.toLowerCase();
    const nameLower = userName?.toLowerCase();
    const plMatch =
      (Array.isArray(dag.plEmail) &&
        dag.plEmail.some((email) => email?.toLowerCase() === emailLower)) ||
      (Array.isArray(dag.plFromProject) &&
        nameLower &&
        dag.plFromProject.some((name) => name?.toLowerCase() === nameLower));

    if (plMatch && startDate >= today && startDate <= tenDaysFromNow) {
      const prepStatus = dag.voorbereidingsstatus || "";
      const hasPrep =
        dag.productieVooruit ||
        prepStatus.toLowerCase().includes("ingevuld") ||
        prepStatus.toLowerCase().includes("voorbereid");

      if (!hasPrep) {
        items.push({
          id: `draaidag-noprep-${dag.id}`,
          type: "draaidag_no_prep",
          group: "workflow",
          severity: daysUntil <= 3 ? "red" : "orange",
          title: dag.naam || dag.projectnaam || "Draaidag",
          subtitle: `${formatRelativeLabel(daysUntil)} - briefing voor productie ontbreekt`,
          link: "/dashboard",
          recordId: dag.id,
          sortDate: startDate.toISOString(),
        });
      }

      const callsheetNodig = String(dag.callsheetNodig || "").toLowerCase() === "ja";
      if (dag.productieVooruit && callsheetNodig && Number(dag.aantalCallsheets || 0) === 0) {
        items.push({
          id: `draaidag-callsheet-${dag.id}`,
          type: "callsheet_needed",
          group: "workflow",
          severity: daysUntil <= 3 ? "red" : "orange",
          title: dag.naam || dag.projectnaam || "Draaidag",
          subtitle: `${formatRelativeLabel(daysUntil)} - callsheet moet nog aangemaakt worden`,
          link: "/dashboard",
          recordId: dag.id,
          sortDate: startDate.toISOString(),
        });
      }
    }

    if (startDate >= today && startDate <= fiveDaysFromNow) {
      const inCrew =
        (Array.isArray(dag.crewEmail) &&
          dag.crewEmail.some((email) => email?.toLowerCase() === emailLower)) ||
        (Array.isArray(dag.crewNaam) &&
          userName &&
          dag.crewNaam.some((name) => name?.toLowerCase() === userName?.toLowerCase()));

      if (inCrew) {
        items.push({
          id: `draaidag-upcoming-${dag.id}`,
          type: "draaidag_upcoming",
          group: "persoonlijk",
          severity: "blue",
          title: dag.naam || dag.projectnaam || "Draaidag",
          subtitle: `${formatRelativeLabel(daysUntil)} - ${dag.locatie || "Locatie onbekend"}`,
          link: "/dashboard",
          recordId: dag.id,
          sortDate: startDate.toISOString(),
        });
      }
    }
  }

  return items;
};

const computeFeedbackNotifications = (feedbackItems = []) =>
  feedbackItems.flatMap((fb) => {
    const base = {
      link: "/my-feedback",
      recordId: String(fb.id),
      title: `Feedback: ${fb.tool_name}`,
      group: "feedback",
    };

    if (fb.pipeline_status === "awaiting_review") {
      return [
        {
          ...base,
          id: `feedback-review-${fb.id}`,
          type: "feedback_review",
          severity: "orange",
          subtitle: "Plan klaar, wacht op jouw goedkeuring",
        },
      ];
    }

    if (fb.pipeline_status === "done" || fb.pipeline_status === "merged_to_main") {
      return [
        {
          ...base,
          id: `feedback-done-${fb.id}`,
          type: "feedback_done",
          severity: "blue",
          subtitle: "Je feedback is geimplementeerd",
        },
      ];
    }

    if (fb.pipeline_status === "pr_open" || fb.pipeline_status === "merged_to_develop") {
      return [
        {
          ...base,
          id: `feedback-progress-${fb.id}`,
          type: "feedback_progress",
          severity: "blue",
          subtitle: fb.pipeline_status === "pr_open" ? "PR aangemaakt" : "Op test omgeving",
        },
      ];
    }

    if (["plan_failed", "pr_failed", "validation_rejected"].includes(fb.pipeline_status)) {
      return [
        {
          ...base,
          id: `feedback-failed-${fb.id}`,
          type: "feedback_failed",
          severity: "red",
          subtitle: "Verwerking mislukt",
        },
      ];
    }

    return [];
  });

const computeQaNotifications = (qaItems = []) => {
  const items = [];
  for (const qa of qaItems) {
    const findings = qa.total_findings || 0;
    const blockers = qa.blockers || 0;
    const filename = qa.video_filename || `Video #${qa.video_id}`;
    const severity = blockers > 0 ? "orange" : "blue";
    const subtitle = findings === 0
      ? `Geen bevindingen gevonden`
      : `${findings} bevinding${findings === 1 ? "" : "en"}${blockers > 0 ? ` (${blockers} hoog)` : ""}`;
    items.push({
      id: `qa-done-${qa.id}`,
      type: "qa_complete",
      group: "workflow",
      severity,
      title: `QA klaar: ${filename}`,
      subtitle,
      link: `/video-qa?review=${qa.id}`,
      recordId: String(qa.id),
      sortDate: qa.created_at,
    });
  }
  return items;
};

// PL-todo's (feedback ontbreekt bij een v2/DEF-taak die vandaag/morgen start).
const computePlTodoNotifications = (items = []) =>
  items.map((t) => ({
    id: `pl-todo-${t.id}`,
    type: t.soort || "pl_todo",
    group: "workflow",
    severity: t.urgentie >= 2 ? "red" : "orange",
    title: `${t.titel}: ${t.taskName || ""}`.trim(),
    subtitle: [t.projectNumber && t.projectName ? `${t.projectNumber} · ${t.projectName}` : t.projectName,
      t.assignee, t.wanneer].filter(Boolean).join(" — "),
    link: t.link || "/dagstart",
    sortDate: t.startDate || null,
  }));

// 'Crew informeren' vanaf een callsheet (15 sep): melding voor ieder crewlid, 14 dagen.
const computeCallsheetMeldingen = (items = []) =>
  items.map((m) => ({
    id: `callsheet-melding-${m.id}`,
    type: "callsheet_update",
    group: "workflow",
    severity: "orange",
    title: `Callsheet bijgewerkt: ${m.titel || ""}`.trim(),
    subtitle: [m.datum, m.bericht].filter(Boolean).join(" — "),
    link: `/callsheets/${m.callsheetId}`,
    sortDate: m.tijd || null,
  }));

// Verslag + actiepunten na een externe opgenomen meeting (engine: meeting_notificaties).
const computeMeetingMeldingen = (items = []) =>
  items.map((m) => ({
    id: `meeting-verslag-${m.id}`,
    type: "meeting_verslag",
    group: "workflow",
    severity: "blue",
    title: `Verslag externe meeting: ${m.titel || ""}`.trim(),
    subtitle: [m.datum, m.actiepunten ? `${m.actiepunten} actiepunt${m.actiepunten === 1 ? "" : "en"}` : null].filter(Boolean).join(" — "),
    link: m.meetingId ? `/MeetingDashboard/${m.meetingId}` : "/MeetingDashboard",
    sortDate: m.tijd || null,
  }));

// Bestanden delen: ongelezen bestanden die een collega met je deelde (engine filtert op ontvanger).
export const computeBestandMeldingen = (items = []) =>
  items.map((b) => ({
    id: `bestand-gedeeld-${b.id}`,
    type: "bestand_gedeeld",
    group: "persoonlijk",
    severity: "blue",
    title: b.is_map
      ? `${b.afzender_naam || b.afzender || "Een collega"} deelde de map '${b.naam || ""}' (${b.aantal || 0} ${Number(b.aantal) === 1 ? "bestand" : "bestanden"})`
      : `${b.afzender_naam || b.afzender || "Een collega"} deelde een bestand: ${b.naam || ""}`.trim(),
    subtitle: b.notitie || "Open om te downloaden",
    link: `/bestanden?id=${encodeURIComponent(b.id)}`,
    recordId: String(b.id),
    sortDate: b.tijd || null,
  }));

// Overleg-voorbereiding: invulblok open (vanaf het pakketmoment), de werkdag ervoor nog eens als herinnering.
// Engine filtert al op deelnemer (vertrouwelijke types zoals MT-punt komen nooit bij niet-deelnemers) en
// laat de regel vallen zodra je indient. Een ander id voor de herinnering: wie de eerste wegklikte, ziet hem terug.
export const computeOverlegMeldingen = (items = []) =>
  items.map((it) => ({
    id: it.herinnering ? `overleg-invulblok-herinnering-${it.sessie_id}` : `overleg-invulblok-${it.sessie_id}`,
    type: "overleg_invulblok",
    group: "persoonlijk",
    severity: it.herinnering ? "orange" : "blue",
    title: it.titel || `Vul je invulblok in voor ${it.type_naam || "het overleg"}`,
    subtitle: it.herinnering ? "Nog niet ingevuld — twee minuten" : "Invulblok staat open — twee minuten",
    link: it.link || `/overleg/sessie/${it.sessie_id}#invulblok`,
    recordId: String(it.sessie_id),
    sortDate: it.gepland_start_nl || null,
  }));

// "Taak staat al op Afgerond" (9 okt): alleen voor de planner(s) en de uitvoerder (engine zet voor_mij).
export const computeAfgerondSignaalMeldingen = (data) =>
  (data?.beschikbaar ? data.taken || [] : [])
    .filter((t) => t.voor_mij)
    .map((t) => ({
      id: `taak-afgerond-signaal-${t.id}`,
      type: "taak_afgerond_signaal",
      group: "workflow",
      severity: "orange",
      title: `Staat al op Afgerond: ${t.naam || ""}`.trim(),
      subtitle: [t.projectnummer, (t.uitvoerders || []).join(", "), t.start].filter(Boolean).join(" — ")
        || "Onzichtbaar in de planning",
      link: "/dagstart",
      recordId: t.id,
      sortDate: t.start || null,
    }));

const normalizeWorkflowNotifications = (items = []) =>
  items.map((item) => ({
    group: "workflow",
    severity: "orange",
    link: "/dashboard",
    ...item,
  }));

async function readJsonFromSettled(result) {
  if (result.status !== "fulfilled") return null;
  if (!result.value?.ok) return null;
  try {
    return await result.value.json();
  } catch {
    return null;
  }
}

export function useNotifications(userEmail, userName) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const dismissedRef = useRef(new Set());
  const intervalRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);

    try {
      const results = await Promise.allSettled([
        authFetch(`${BACKEND_URL}/Api/my-tasks?email=${encodeURIComponent(userEmail)}`),
        authFetch(`${BACKEND_URL}/Api/projects`),
        authFetch(`${BACKEND_URL}/Api/draaidagen/upcoming?weeks=3`),
        authFetch(`${BACKEND_URL}/Api/feedback/my?limit=20`),
        authFetch(`${BACKEND_URL}/Api/my-deadlines?email=${encodeURIComponent(userEmail)}`),
        authFetch(`${BACKEND_URL}/Api/notifications/workflow`),
        authFetch(`${BACKEND_URL}/Api/pl-todos`),
        authFetch(`${BACKEND_URL}/Api/callsheets/meldingen/mijn`),
        authFetch(`${AI_ENGINE_URL}/api/v1/video-search/qa/notifications?email=${encodeURIComponent(userEmail)}&hours=24`),
        authFetch(`${AI_ENGINE_URL}/api/v1/meetings/meldingen/mijn`),
        authFetch(`${BACKEND_URL}/Api/overleg/invulblokken`),
        authFetch(`${BACKEND_URL}/Api/bestanden/meldingen`),
        authFetch(`${BACKEND_URL}/Api/taak-signaal/afgerond`),
      ]);

      const [tasksData, projectsData, draaidagenData, feedbackData, deadlinesData, workflowData, plTodoData, callsheetMeldingData, qaData, meetingMeldingData, overlegData, bestandData, afgerondData] =
        await Promise.all(results.map(readJsonFromSettled));

      const computed = dedupeNotifications([
        ...computeTaskNotifications(tasksData?.tasks || []),
        ...computeProjectNotifications(projectsData?.projects || [], userName),
        ...computeDeadlineNotifications(deadlinesData || {}),
        ...computeDraaidagNotifications(draaidagenData?.draaidagen || [], userEmail, userName),
        ...normalizeWorkflowNotifications(workflowData?.notifications || []),
        ...computePlTodoNotifications(plTodoData?.todos || []),
        ...computeCallsheetMeldingen(callsheetMeldingData?.meldingen || []),
        ...computeMeetingMeldingen(meetingMeldingData?.meldingen || []),
        ...computeOverlegMeldingen(overlegData?.items || []),
        ...computeBestandMeldingen(bestandData?.meldingen || []),
        ...computeAfgerondSignaalMeldingen(afgerondData),
        ...computeFeedbackNotifications(Array.isArray(feedbackData) ? feedbackData : []),
        ...computeQaNotifications(qaData?.notifications || []),
      ]).filter((item) => !dismissedRef.current.has(item.id));

      setNotifications(sortNotifications(computed));
    } catch (err) {
      console.error("[useNotifications] Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [userEmail, userName]);

  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [fetchNotifications]);

  const dismiss = useCallback((notificationId) => {
    dismissedRef.current.add(notificationId);
    setNotifications((prev) => prev.filter((notification) => notification.id !== notificationId));
  }, []);

  const dismissAll = useCallback(() => {
    notifications.forEach((notification) => dismissedRef.current.add(notification.id));
    setNotifications([]);
  }, [notifications]);

  return { notifications, loading, dismiss, dismissAll, refetch: fetchNotifications };
}

export default useNotifications;
